import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

type VariantId = "A" | "B" | "C" | "D";

interface Variant {
  id: VariantId;
  textKey: string;        // i18n key for the label
  height: string;         // tailwind height class
  textSize: string;       // tailwind text size
  bg: string;             // tailwind bg / gradient class
  textColor: string;      // tailwind text color class
}

const VARIANTS: Variant[] = [
  // A — control: same as before
  { id: "A", textKey: "hero.stickyCta",           height: "h-12", textSize: "text-base", bg: "bg-gradient-accent",                              textColor: "text-accent-foreground" },
  // B — different copy (benefit-led)
  { id: "B", textKey: "hero.stickyCtaVariantB",   height: "h-12", textSize: "text-base", bg: "bg-gradient-accent",                              textColor: "text-accent-foreground" },
  // C — bigger size, urgency copy
  { id: "C", textKey: "hero.stickyCtaVariantC",   height: "h-14", textSize: "text-lg font-bold", bg: "bg-gradient-accent",                       textColor: "text-accent-foreground" },
  // D — different color (emerald), short copy
  { id: "D", textKey: "hero.stickyCtaVariantD",   height: "h-12", textSize: "text-base font-bold", bg: "bg-emerald-500 hover:bg-emerald-600",   textColor: "text-white" },
];

const STORAGE_KEY_VARIANT = "tml_sticky_cta_variant";
const STORAGE_KEY_VISITOR = "tml_visitor_id";
const STORAGE_KEY_IMPRESSION = "tml_sticky_cta_impression_logged";

const pickVariant = (): Variant => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_VARIANT) as VariantId | null;
    const found = stored && VARIANTS.find((v) => v.id === stored);
    if (found) return found;
  } catch {/* ignore */}
  const picked = VARIANTS[Math.floor(Math.random() * VARIANTS.length)];
  try { localStorage.setItem(STORAGE_KEY_VARIANT, picked.id); } catch {/* ignore */}
  return picked;
};

const getVisitorId = (): string => {
  try {
    let id = localStorage.getItem(STORAGE_KEY_VISITOR);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(STORAGE_KEY_VISITOR, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
};

/**
 * Sticky bottom CTA shown on mobile after the user scrolls past the hero.
 * Hidden on desktop and when the user is logged in.
 * Runs an A/B/C/D experiment (copy / size / color) and logs impressions + clicks
 * to the mobile_cta_events table so we can measure which variant lifts /auth visits.
 */
const MobileStickyCTA = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [variant] = useState<Variant>(() => pickVariant());
  const impressionLoggedRef = useRef(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Log a single impression per session per variant
  useEffect(() => {
    if (!visible || user || impressionLoggedRef.current) return;
    impressionLoggedRef.current = true;
    const sessionKey = `${STORAGE_KEY_IMPRESSION}_${variant.id}`;
    try {
      if (sessionStorage.getItem(sessionKey)) return;
      sessionStorage.setItem(sessionKey, "1");
    } catch {/* ignore */}
    supabase.functions
      .invoke("track-mobile-cta", {
        body: { variant: variant.id, event_type: "impression", visitor_id: getVisitorId() },
      })
      .then(() => {}, () => {});
  }, [visible, user, variant.id]);

  const handleClick = async () => {
    try {
      await supabase.functions.invoke("track-mobile-cta", {
        body: { variant: variant.id, event_type: "click", visitor_id: getVisitorId() },
      });
    } catch {/* ignore */}
    navigate(user ? "/dashboard" : "/auth?tab=register");
  };

  if (user) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-40 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 bg-background/95 backdrop-blur-md border-t border-border lg:hidden transition-transform duration-300 ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
      data-cta-variant={variant.id}
    >
      <Button
        size="lg"
        className={`w-full gap-2 rounded-full border-0 shadow-elevated transition-all ${variant.height} ${variant.textSize} ${variant.bg} ${variant.textColor} hover:opacity-95`}
        onClick={handleClick}
      >
        <Zap className="w-4 h-4" />
        {t(variant.textKey)}
      </Button>
    </div>
  );
};

export default MobileStickyCTA;